<template>
  <AppLayout title="Calendar">
    <CardBase>
      <CardHeadingWithDescriptionAction :title="currentMonthYearString">
        <template #actions>
          <ButtonGroupPreviousNext
            @clickedLeft="previousMonth"
            @clickedRight="nextMonth"
          />
        </template>
      </CardHeadingWithDescriptionAction>
      <div class="m-2 space-y-2 space-x-2">
        <Link
          v-for="(plane, index) in planes"
          :key="plane.id"
          :href="route('planes.show', plane)"
        >
          <BadgeBase
            size="lg"
            :class="{ 'ml-2': index === 0 }"
            :color="getColorNameOfPlane(plane.id)"
          >
            {{ plane.tail_number }}
          </BadgeBase>
        </Link>
      </div>
      <div
        v-if="props.pilots.length"
        class="mx-2 mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md px-2 py-1 transition-colors"
        :class="legendClasses"
        @dragover.prevent="
          crewDrag.isRemoveZone.value && crewDrag.allowMove($event)
        "
        @dragenter.prevent="
          crewDrag.isRemoveZone.value && crewDrag.enter('legend')
        "
        @dragleave="crewDrag.leave('legend')"
        @drop.prevent="crewDrag.dropOnRemoveZone()"
      >
        <SpanText size="text-xs" color="text-gray-500">{{
          legendLabel
        }}</SpanText>
        <span
          v-for="pilot in props.pilots"
          :key="pilot.id"
          class="inline-flex items-center"
          :class="canDragPilots && 'cursor-grab select-none'"
          :draggable="canDragPilots"
          @dragstart="crewDrag.startDrag(pilot, null, $event)"
          @dragend="crewDrag.endDrag()"
        >
          <span
            class="rounded px-1 text-xs font-semibold leading-4"
            :class="pilotChipClasses(pilot.calendar_color)"
            >{{ initials(pilot.name) }}</span
          >
          <SpanText size="text-xs" class="ml-1">{{ pilot.name }}</SpanText>
        </span>
      </div>
      <div class="grid grid-cols-7 pt-2">
        <div v-for="day in daysOfWeek" class="col-span-1 text-center mb-1">
          <ParagraphText>{{ day }}</ParagraphText>
        </div>
        <div
          v-for="day in emptyDays"
          class="col-span-1 border dark:border-gray-700"
        ></div>
        <div
          v-for="day in props.days"
          class="col-span-1 border dark:border-gray-700"
        >
          <CalendarDay :day="day.date" :events="getEventsForDay(day)" />
        </div>
      </div>
    </CardBase>
  </AppLayout>
</template>

<script setup>
import { computed, provide } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { bgColorClasses } from '@/Common/util/colorMap'
import { Link } from '@inertiajs/inertia-vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import CardBase from '@/Common/Layout/Containers/CardBase.vue'
import CardHeadingWithDescriptionAction from '@/Common/Headings/CardHeadings/CardHeadingWithDescriptionAction.vue'
import ButtonGroupPreviousNext from '@/Common/Elements/ButtonGroups/ButtonGroupPreviousNext.vue'
import ParagraphText from '@/Common/Text/ParagraphText.vue'
import CalendarDay from './CalendarDay.vue'
import BadgeBase from '@/Common/Elements/Badges/BadgeBase.vue'
import SpanText from '@/Common/Text/SpanText.vue'
import { LocationMarkerIcon } from '@heroicons/vue/solid'
import {
  crewChips,
  crewSummary,
  initials,
  pilotChipClasses,
} from './pilotColors'
import { useCrewDrag } from '../Trips/useCrewDrag'

const props = defineProps({
  filters: Object,
  days: Array,
  planes: Array,
  pilots: Array,
  currentMonthYearString: String,
})

// Calendar
const currentMonth = computed(() => Number(props.filters.month) - 1)
const currentYear = computed(() => Number(props.filters.year))

const emptyDays = computed(() => {
  const firstDayOfWeek = new Date(
    currentYear.value,
    currentMonth.value
  ).getDay()

  return firstDayOfWeek
})

const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Colors and events
const colorMap = {
  0: 'indigo',
  1: 'green',
  2: 'yellow',
  3: 'blue',
  4: 'pink',
  5: 'red',
  6: 'purple',
}

function getPlaneIndexById(plane_id) {
  return props.planes.findIndex((plane) => plane.id === plane_id)
}

function getColorNameOfPlane(plane_id) {
  return colorMap[getPlaneIndexById(plane_id)] || 'gray'
}

function getColorOfPlane(plane_id) {
  return 'bg-' + getColorNameOfPlane(plane_id) + '-100'
}

// Crew

// Only people responsible for crew coverage see the "No SIC" warnings, and
// only they can drag pilots onto flights
const managedPlaneIds = computed(() =>
  props.planes
    .filter((plane) => plane.can_manage_pilots)
    .map((plane) => plane.id)
)

const canDragPilots = computed(() => managedPlaneIds.value.length > 0)

function pilotPlaneIds(pilot) {
  return props.pilots.find((el) => el.id === pilot.id)?.plane_ids || []
}

// A flight only takes pilots of its own plane, and only where the viewer manages crew
const crewDrag = useCrewDrag({
  canDrop: (leg, pilot) =>
    managedPlaneIds.value.includes(leg.trip.plane_id) &&
    pilotPlaneIds(pilot).includes(leg.trip.plane_id),
})

provide('crewDrag', crewDrag)

const legendLabel = computed(() => {
  if (crewDrag.isRemoveZone.value)
    return `Drop here to take ${crewDrag.drag.value.pilot.name} off that flight`

  if (crewDrag.drag.value) return 'Drop on a flight below'

  return canDragPilots.value ? 'Pilots · drag one onto a flight' : 'Pilots'
})

const legendClasses = computed(() => {
  if (!crewDrag.isRemoveZone.value) return ''

  return crewDrag.isOver('legend')
    ? 'bg-red-50 ring-2 ring-inset ring-red-400 dark:bg-red-900/40'
    : 'bg-red-50/50 dark:bg-red-900/20'
})

function getEventsForDay(day) {
  const legs = day.legs.map((leg) => {
    return {
      time: leg.formatted_departure_time,
      color: bgColorClasses(getColorOfPlane(leg.trip.plane_id)),
      href: route('trips.show', leg.trip),
      name:
        leg.departure_airport.display_code +
        ' to ' +
        leg.arrival_airport.display_code,
      chips: crewChips(
        leg.pilots,
        managedPlaneIds.value.includes(leg.trip.plane_id)
      ),
      title: crewSummary(leg.pilots),
      // For dragging pilots on and off the flight
      leg,
      canManageCrew: managedPlaneIds.value.includes(leg.trip.plane_id),
    }
  })

  const awayLocations = day.awayLocations.map((awayLocation) => {
    return {
      // time: 'Away',
      icon: LocationMarkerIcon,
      color: bgColorClasses(getColorOfPlane(awayLocation.plane.id)),
      href: route('trips.show', awayLocation.leg.trip),
      name: awayLocation.leg.arrival_airport.display_code,
    }
  })

  const holds = day.holds.map((hold) => {
    return {
      time: 'Hold',
      color: bgColorClasses(getColorOfPlane(hold.plane_id)),
      href: route('planes.holds.index', hold.plane),
      name: hold.title,
    }
  })

  return legs.concat(holds).concat(awayLocations)
}

// Clicking arrows to change month

function nextMonth() {
  let newMonth = Number(props.filters.month) + 1
  let newYear = currentYear.value
  if (newMonth > 12) {
    newMonth = 1
    newYear = newYear + 1
  }
  changeMonth(newMonth, newYear)
}

function previousMonth() {
  let newMonth = Number(props.filters.month) - 1
  let newYear = currentYear.value
  if (newMonth < 1) {
    newMonth = 12
    newYear = newYear - 1
  }
  changeMonth(newMonth, newYear)
}

function changeMonth(newMonth, newYear) {
  Inertia.get(
    route('calendar.index', {
      month: newMonth,
      year: newYear,
    })
  )
}
</script>
